import { useMemo } from 'react';
import { Select, SelectOption } from './Select';
import { ISO_CURRENCIES } from '@/lib/iso/currencies';

interface CurrencySelectProps {
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  disabled?: boolean;
  // Limit to a curated subset (codes uppercase). Defaults to full list.
  only?: string[];
  // Short-form trigger label: show only the code (e.g. "JPY"), not the
  // full "Japanese yen". Used in tight header chrome. Default false.
  compact?: boolean;
  width?: 'auto' | 'full';
  className?: string;
  ariaLabel?: string;
}

export function CurrencySelect({
  value,
  onChange,
  placeholder,
  disabled,
  only,
  compact = false,
  width = 'full',
  className,
  ariaLabel,
}: CurrencySelectProps) {
  const options: SelectOption[] = useMemo(() => {
    const list = only
      ? ISO_CURRENCIES.filter((c) => only.includes(c.code))
      : ISO_CURRENCIES;
    return list.map((c) => ({
      value: c.code,
      label: compact ? c.code : `${c.code} · ${c.name}`,
      hint: compact ? undefined : c.symbol,
      prefix: (
        <span className="text-[11px] font-semibold text-ink-500 num">
          {c.symbol}
        </span>
      ),
      searchAliases: [c.name, c.symbol, ...(c.aliases ?? [])],
    }));
  }, [only, compact]);

  return (
    <Select
      value={value.toUpperCase()}
      onChange={(v) => onChange(v.toUpperCase())}
      options={options}
      placeholder={placeholder}
      disabled={disabled}
      width={width}
      className={className}
      ariaLabel={ariaLabel}
    />
  );
}
